import React, { useCallback, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { useAppSelector } from "app/hooks";
import { DEFAULT_EDOCS_SORT_BY } from "constants/clinics";
import { selectCurrentPractice } from "features/auth/auth-slice";
import usePatientHeader from "features/patients/patient-header/use-patient-header.hook";
import useBoxTokenProvider from "hooks/use-box-token-provider.hook";
import { useBoxAssets } from "utils/box-assets.util";

// Declare the Box global variable and its types
declare global {
  interface Box {
    ContentExplorer: any;
  }
  interface Window {
    Box: Box;
  }
}

type BoxContentExplorerProps = {
  token: string;
  folderId: string;
  onSelection?: (files: any[]) => void;
  logoUrl?: string;
  options?: any;
  refreshTrigger?: number;
  autoPreviewFileName?: string;
  onAutoPreviewComplete?: () => void;
};

const parseSortSetting = (sortSetting: string = DEFAULT_EDOCS_SORT_BY) => {
  const [sortBy, direction] = sortSetting.split("_");
  return {
    sortBy: sortBy as "name" | "date",
    sortDirection: direction.toUpperCase() as "ASC" | "DESC"
  };
};

const BoxContentExplorer = ({
  token,
  folderId,
  logoUrl,
  onSelection,
  options,
  refreshTrigger,
  autoPreviewFileName,
  onAutoPreviewComplete
}: BoxContentExplorerProps) => {
  const containerRef = useRef<HTMLDivElement>();
  const explorerRef = useRef<any>(null);
  const rootFolderIdRef = useRef<string>(folderId);
  const currentFolderIdRef = useRef<string>(folderId);
  const lastRefreshTriggerRef = useRef<number | undefined>(refreshTrigger);
  const { isHeaderCollapsed } = usePatientHeader();
  const { isLoaded, isLoading, error } = useBoxAssets();
  const boxTokenProvider = useBoxTokenProvider(token);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [height, setHeight] = useState(window.innerHeight - 463);

  // Resolve the Box access token to a string before showing the explorer
  useEffect(() => {
    let active = true;
    boxTokenProvider()
      .then((resolvedToken) => {
        if (active) setAccessToken(resolvedToken);
      })
      .catch(() => {
        if (active && token) setAccessToken(token);
      });
    return () => {
      active = false;
    };
  }, [boxTokenProvider, token]);

  const currentPractice = useAppSelector(selectCurrentPractice);
  const { sortBy, sortDirection } = parseSortSetting(
    currentPractice?.edocs_sort_by
  );

  const adjustHeight = useCallback(() => {
    if (containerRef.current) {
      const top = containerRef.current?.getBoundingClientRect().top || 0;
      setHeight(window.innerHeight - top - 40);
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [adjustHeight, isHeaderCollapsed]);

  useEffect(() => {
    window.addEventListener("resize", adjustHeight);

    return () => {
      window.removeEventListener("resize", adjustHeight);
    };
  }, [adjustHeight]);

  const showExplorer = useCallback(() => {
    if (!isLoaded || !window.Box || !containerRef.current || !accessToken)
      return;

    // Detect patient switch — reset tracked folder to new root
    if (rootFolderIdRef.current !== folderId) {
      rootFolderIdRef.current = folderId;
      currentFolderIdRef.current = folderId;
    }

    const { ContentExplorer } = window.Box;
    const explorer = new ContentExplorer();
    explorerRef.current = explorer;

    if (onSelection) {
      explorer.addListener("select", onSelection);
    }
    explorer.addListener("navigate", (folderItem: any) => {
      if (folderItem?.id) {
        currentFolderIdRef.current = folderItem.id;
      }
    });
    boxTokenProvider().then((accessToken) => {
      explorer.show(currentFolderIdRef.current || folderId, accessToken, {
        container: containerRef.current,
        logoUrl,
        sortBy,
        sortDirection,
        ...options
      });
    });
  }, [
    folderId,
    accessToken,
    logoUrl,
    onSelection,
    options,
    isLoaded,
    sortBy,
    sortDirection
  ]);

  // Initial mount
  useEffect(() => {
    showExplorer();

    return () => {
      if (explorerRef.current) {
        explorerRef.current.hide();
        explorerRef.current = null;
      }
    };
  }, [showExplorer]);

  // In-place refresh when refreshTrigger changes (after rotation)
  useEffect(() => {
    if (refreshTrigger === undefined || refreshTrigger === 0) return;
    if (refreshTrigger === lastRefreshTriggerRef.current) return;

    lastRefreshTriggerRef.current = refreshTrigger;

    if (explorerRef.current) {
      explorerRef.current.hide();
      explorerRef.current = null;
    }
    showExplorer();
  }, [refreshTrigger, showExplorer]);

  // Auto-open file preview after refresh
  useEffect(() => {
    if (!autoPreviewFileName || !containerRef.current) return;

    let attempts = 0;
    const maxAttempts = 15;
    const interval = setInterval(() => {
      attempts++;
      if (!containerRef.current || attempts > maxAttempts) {
        clearInterval(interval);
        onAutoPreviewComplete?.();
        return;
      }

      // Search for a clickable element with the file name in the Box explorer
      const buttons = containerRef.current.querySelectorAll(
        ".be-item-name button, .bce-item-name button"
      );
      for (const btn of buttons) {
        if (btn.textContent?.trim() === autoPreviewFileName) {
          (btn as HTMLElement).click();
          clearInterval(interval);
          onAutoPreviewComplete?.();
          return;
        }
      }
    }, 300);

    return () => clearInterval(interval);
  }, [autoPreviewFileName, onAutoPreviewComplete]);

  // Show loading state while Box assets are loading
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ height }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state if Box assets failed to load
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ height }}
      >
        Error loading Box components: {error.message}
      </Box>
    );
  }

  return <Box ref={containerRef} className="container" style={{ height }} />;
};

export default BoxContentExplorer;
